import { useEffect, useState } from 'react'
import { CategoryIcon, colorVar } from '../components/CategoryIcon'
import { CategoryEditor, type CategoryDraft } from '../components/CategoryEditor'
import { ApiError, api, type Category, type MerchantSuggestion, type Store } from '../lib/api'
import { M_PER_MILE, formatDistance, haversine, type Fix } from '../lib/location'
import { useStore } from '../lib/store'
import './AddReminder.css'

const SUGGESTIONS = [
  'Milk',
  'Bread',
  'Toothpaste',
  'Eggs',
  'Protein Powder',
  'Dog Food',
  'Bananas',
]

// How far the user must travel before nearby-store results are searched again.
// Roughly a city block: far enough that the list stays still while they read it,
// close enough that driving across town re-anchors the search.
const SEARCH_REANCHOR_M = 250

const MIN_M = 402 // 0.25 mi
const MAX_M = 16093 // 10 mi

/**
 * Generic merchant classes are labelled "Any hardware store", so a sentence that
 * supplies its own "any" reads "matches any Any hardware store". Brand labels
 * ("Best Buy") never carry the prefix and must survive untouched — which is also
 * why this doesn't lowercase: what follows "Any " is already lowercase, and
 * lowercasing a brand would give "no best buy found".
 */
function bareLabel(label: string): string {
  return label.replace(/^any\s+/i, '')
}

export function AddReminder({ onDone, onCancel, onUpgrade }: {
  onDone: () => void
  onCancel: () => void
  onUpgrade: () => void
}) {
  const { fix, user, refresh, households, categories, createCategory } = useStore()
  const [step, setStep] = useState<1 | 2>(1)
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState<Category>('')
  const [radius, setRadius] = useState(user?.default_radius_m ?? 3219)
  const [householdId, setHouseholdId] = useState<string>('')

  // Creating a category happens here, in place — leaving the step to go to
  // Settings and back would lose the half-typed reminder.
  const [creating, setCreating] = useState(false)
  const [catBusy, setCatBusy] = useState(false)
  const [catError, setCatError] = useState<string | null>(null)

  const [merchants, setMerchants] = useState<MerchantSuggestion[]>([])
  const [chosen, setChosen] = useState<MerchantSuggestion | null>(null)
  const [stores, setStores] = useState<Store[]>([])
  // `loading` is the store lookup only. Saving has its own flag: the two used to
  // share one, which meant Overpass — routinely 40s, often down — held the Save
  // button hostage even though a reminder binds to a merchant class and never to
  // the store list underneath it.
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Categories arrive from the store, so there is nothing sensible to preselect
  // on mount. This also re-homes the selection if the chosen category vanishes.
  useEffect(() => {
    if (categories.length === 0) return
    if (categories.some((c) => c.slug === category)) return
    setCategory(categories[0]!.slug)
  }, [categories, category])

  // Store lookups are anchored to a coarse origin rather than the live fix.
  // Keying them on `fix` meant every GPS reading re-ran both searches, so the
  // list re-ordered and flickered under the user's finger (APP-05) and the
  // merchant reset below fired over and over (APP-08). Walking a few metres is
  // not a new search; crossing a neighbourhood is.
  const [origin, setOrigin] = useState<Fix | null>(null)
  useEffect(() => {
    if (!fix) return
    setOrigin((prev) =>
      !prev || haversine(prev.lat, prev.lon, fix.lat, fix.lon) > SEARCH_REANCHOR_M ? fix : prev,
    )
  }, [fix])

  // Step 2 needs candidate merchant classes for the chosen category.
  useEffect(() => {
    if (step !== 2 || !category) return
    let cancelled = false
    setLoading(true)
    setError(null)
    // The typed title steers which merchant classes lead the list: "Toothpaste"
    // should offer a pharmacy even when it was filed under Errands (APP-06).
    const hint = title.trim() || undefined
    const load = origin
      ? api.brands(category, origin.lat, origin.lon, hint)
      : api.suggestions(category, hint)
    load
      .then((list) => {
        if (cancelled) return
        setMerchants(list)
        // Only *default* the selection — never overwrite one the user made.
        // This line used to reassign `list[0]` on every refetch, which is how a
        // deliberate choice silently became whichever brand happened to have
        // the most cached rows (APP-08).
        setChosen((prev) => {
          if (prev && list.some((m) => m.merchant_class === prev.merchant_class)) return prev
          return list[0] ?? null
        })
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [step, category, origin, title])

  // Resolving one merchant class to many stores *is* the differentiator, so
  // show the user every match rather than making them pin one.
  useEffect(() => {
    if (!chosen || !origin) {
      setStores([])
      return
    }
    let cancelled = false
    setLoading(true)
    api
      .findStores(chosen.merchant_class, origin.lat, origin.lon, 8)
      .then((s) => !cancelled && setStores(s))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [chosen, origin])

  async function addCategory(draft: CategoryDraft) {
    setCatBusy(true)
    setCatError(null)
    try {
      const created = await createCategory(draft)
      setCategory(created.slug)
      setCreating(false)
    } catch (e) {
      setCatError(
        e instanceof ApiError && e.isDuplicateCategory
          ? 'You already have a category with that name.'
          : e instanceof Error
            ? e.message
            : String(e),
      )
    } finally {
      setCatBusy(false)
    }
  }

  async function save() {
    if (!chosen || saving) return
    setSaving(true)
    setError(null)
    try {
      await api.createReminder({
        title: title.trim(),
        category,
        merchant_class: chosen.merchant_class,
        merchant_label: chosen.label,
        radius_m: radius,
        ...(householdId ? { household_id: householdId } : {}),
      })
      await refresh()
      onDone()
    } catch (e) {
      if (e instanceof ApiError && e.isTierLimit) {
        onUpgrade()
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const miles = (radius / M_PER_MILE).toFixed(radius / M_PER_MILE < 1 ? 2 : 1)

  return (
    <>
      <header className="nav nav--plain">
        <button
          className="icon-btn"
          onClick={() => (step === 1 ? onCancel() : setStep(1))}
          aria-label="Back"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <h1>{step === 1 ? 'New Reminder' : 'Choose Location'}</h1>
        <button className="icon-btn" onClick={onCancel} aria-label="Close">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="scroll">
        {error && <p className="banner banner--error" role="alert">{error}</p>}

        {step === 1 ? (
          <>
            <div className="field rise">
              <label htmlFor="what">What do you need?</label>
              <input
                id="what"
                autoFocus
                value={title}
                placeholder="iPad charger"
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && title.trim() && setStep(2)}
              />
            </div>

            <h2 className="section-label">Suggestions</h2>
            <div className="suggest">
              {SUGGESTIONS.map((s) => (
                <button key={s} className="chip" onClick={() => setTitle(s)}>
                  {s}
                </button>
              ))}
            </div>

            <h2 className="section-label">Category</h2>
            <div className="cats">
              {categories.map((c) => (
                <button
                  key={c.id}
                  className={`cat${category === c.slug ? ' is-on' : ''}`}
                  style={{ ['--cat' as string]: colorVar(c.color) }}
                  onClick={() => setCategory(c.slug)}
                  aria-pressed={category === c.slug}
                >
                  <span className="cat__tile">
                    <CategoryIcon icon={c.icon} color={colorVar(c.color)} size={24} />
                  </span>
                  {c.label}
                </button>
              ))}

              <button
                className={`cat cat--new${creating ? ' is-open' : ''}`}
                onClick={() => {
                  setCatError(null)
                  setCreating((v) => !v)
                }}
                aria-expanded={creating}
              >
                <span className="cat__tile">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 6v12M6 12h12" />
                  </svg>
                </span>
                New
              </button>
            </div>

            {creating && (
              <CategoryEditor
                submitLabel="Create"
                busy={catBusy}
                error={catError}
                autoFocus
                onSubmit={addCategory}
                onCancel={() => {
                  setCreating(false)
                  setCatError(null)
                }}
              />
            )}

            <h2 className="section-label">
              Trigger distance <span className="section-label__value">{miles} mi</span>
            </h2>
            <input
              className="slider"
              type="range"
              min={MIN_M}
              max={MAX_M}
              step={80}
              value={radius}
              aria-label="Trigger distance"
              onChange={(e) => setRadius(Number(e.target.value))}
            />
            <div className="slider__scale">
              <span>0.25 mi</span>
              <span>10 mi</span>
            </div>

            {households.length > 0 && (
              <>
                <h2 className="section-label">Share with</h2>
                <div className="suggest">
                  <button
                    className="chip"
                    aria-pressed={householdId === ''}
                    onClick={() => setHouseholdId('')}
                  >
                    Just me
                  </button>
                  {households.map((h) => (
                    <button
                      key={h.id}
                      className="chip"
                      aria-pressed={householdId === h.id}
                      onClick={() => setHouseholdId(h.id)}
                    >
                      {h.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            <button
              className="btn btn--primary next"
              disabled={!title.trim() || !category}
              onClick={() => setStep(2)}
            >
              Next
            </button>
          </>
        ) : (
          <>
            <h2 className="section-label">Where?</h2>
            <p className="hint hint--lead">
              Pick the kind of place. The reminder follows every store that matches,
              so you don&rsquo;t have to choose a branch.
            </p>
            <div className="suggest" role="group" aria-label="Kind of place">
              {merchants.map((m) => (
                <button
                  key={m.merchant_class}
                  className="chip"
                  aria-pressed={chosen?.merchant_class === m.merchant_class}
                  onClick={() => setChosen(m)}
                >
                  {chosen?.merchant_class === m.merchant_class && (
                    <svg
                      className="chip__tick"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                  {m.label}
                </button>
              ))}
            </div>

            {!fix && (
              <p className="banner">
                No location fix yet, so we can&rsquo;t list nearby stores. The reminder
                still works — it will resolve stores once location is available.
              </p>
            )}

            <h2 className="section-label">
              Matching stores
              {stores.length > 0 && (
                <span className="section-label__value">{stores.length} nearby</span>
              )}
            </h2>
            {stores.length > 0 && (
              <p className="hint hint--lead">
                All of these already count &mdash; nothing here needs tapping.
              </p>
            )}

            {loading && <p className="hint">Searching OpenStreetMap&hellip;</p>}

            {/*
              Deliberately not buttons. A reminder binds to the merchant class
              chosen above, never to one branch, so these rows are a preview of
              what will match. They previously carried the same glass-and-hover
              treatment as tappable cards, which is why testers tapped them and
              reported dead rows (APP-04); `.store` now reads as a readout.
            */}
            <div className="stack">
              {stores.map((s, i) => (
                <div
                  key={s.id}
                  className="store store--readonly glass rise"
                  style={{ animationDelay: `${i * 45}ms` }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ink-2)" strokeWidth="1.7">
                    <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
                    <circle cx="12" cy="10" r="2.4" />
                  </svg>
                  <div className="store__body">
                    <strong>{s.name ?? s.brand ?? 'Store'}</strong>
                    <span>{s.address ?? 'Address unknown'}</span>
                  </div>
                  <span className="store__dist">{formatDistance(s.distance_m)}</span>
                </div>
              ))}
            </div>

            {!loading && stores.length === 0 && chosen && fix && (
              <p className="hint">
                No {bareLabel(chosen.label)} found within 15 miles. The reminder will
                still fire if one turns up as you move.
              </p>
            )}

            <p className="hint multi">
              This reminder matches <strong>any</strong>{' '}
              {chosen ? bareLabel(chosen.label) : 'store'} — not one pinned address.
            </p>

            <button
              className="btn btn--primary next"
              disabled={!chosen || saving}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save Reminder'}
            </button>
          </>
        )}
      </div>
    </>
  )
}
